/**
 * @file SearchableDropdown.js
 * @description A reusable searchable dropdown component that supports multi-select functionality with tagging, filtering, and customizable display options.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useWindowListener } from '../helpers/useWindowListener';
import '../helpers/SearchableDropdown.css';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { getText } from '../../i18n';

const RENDER_BATCH_SIZE = 100;
const EMPTY_ARRAY = [];
const nameCollator = new Intl.Collator();

function getSelectionKey(option, optionIDField) {
    const hasId = optionIDField && option[optionIDField] != null;
    return hasId ? `id:${option[optionIDField]}` : `name:${option.name}`;
}

export function SearchableDropdown({
    placeholderLabel = 'Select Options',
    options,
    optionIDField = null,
    updateOptions,
    savedOptions,
    savedOptionIDField = null,
    disabled = false,
    buttonMode = false,
    buttonLabel = null,
    buttonIcon = null,
    showTags = true,
    maxSelections = null,
    minSelections = null,
    controlledSelection = false,
    exclusiveOptionId = null,
    arrangeTags = false,
    pinnedOptions = EMPTY_ARRAY,
    lockedOptionIds = EMPTY_ARRAY,
    selectAll = false,
    readOnly = false,
}) {
    const [allOptions, setAllOptions] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [renderLimit, setRenderLimit] = useState(RENDER_BATCH_SIZE);
    const dropdownRef = useRef(null);
    const itemsRef = useRef(null);
    const labelRef = useRef(null);
    const loadMoreRef = useRef(null);
    const displayOrderRef = useRef(new Map());

    const pinnedNameSet = useMemo(
        () => new Set(pinnedOptions.map(p => p.name)),
        [pinnedOptions],
    );

    const optionsByKey = useMemo(
        () =>
            new Map(
                allOptions.map(option => [
                    getSelectionKey(option, optionIDField),
                    option,
                ]),
            ),
        [allOptions, optionIDField],
    );

    const isAllOption = option => {
        const optionName = option.name?.toLowerCase() || '';
        return optionName.startsWith('all ') || option?.isPinnedTop;
    };

    const separateAllOptions = optionsArray => {
        const allOptions = optionsArray.filter(isAllOption);
        const regularOptions = optionsArray.filter(
            option => !isAllOption(option),
        );
        return { allOptions, regularOptions };
    };

    const sortOptionsWithAllOnTop = optionsArray => {
        const { allOptions, regularOptions } = separateAllOptions(optionsArray);

        const sortedRegularOptions = [...regularOptions].sort((a, b) => {
            if (a.checked && !b.checked) return -1;
            if (!a.checked && b.checked) return 1;
            if (!a.checked && !b.checked)
                return nameCollator.compare(a.name, b.name);
            return 0;
        });

        return [...allOptions, ...sortedRegularOptions];
    };

    useEffect(() => {
        const getOptionKey = opt => (optionIDField ? opt[optionIDField] : opt);
        const getSavedKey = sel =>
            savedOptionIDField ? sel[savedOptionIDField] : sel;
        const newAllOptions = options.map(opt => ({ ...opt }));

        if (controlledSelection) {
            const savedKeys = new Set((savedOptions || []).map(getSavedKey));
            newAllOptions.forEach(opt => {
                opt.checked = savedKeys.has(getOptionKey(opt));
            });
        } else if (savedOptions) {
            const optionsByKey = new Map();
            newAllOptions.forEach(opt => {
                opt.checked = false;
                const key = getOptionKey(opt);
                if (!optionsByKey.has(key)) optionsByKey.set(key, opt);
            });

            const newSelected = [];
            savedOptions.forEach(savedOpt => {
                const matchedOption = optionsByKey.get(getSavedKey(savedOpt));
                if (matchedOption) {
                    matchedOption.checked = true;
                    newSelected.push(
                        getSelectionKey(matchedOption, optionIDField),
                    );
                }
            });

            setSelectedOptions(newSelected);
        }

        setAllOptions(newAllOptions);
    }, [
        options,
        savedOptions,
        optionIDField,
        savedOptionIDField,
        controlledSelection,
    ]);

    useEffect(() => {
        const toggleItems = () => {
            const willOpen = !itemsRef.current.classList.contains('visible');

            if (willOpen) {
                const sortedOptions = sortOptionsWithAllOnTop(
                    allOptions.filter(o => !pinnedNameSet.has(o.name)),
                );
                displayOrderRef.current = new Map();
                sortedOptions.forEach((option, index) => {
                    const key = getSelectionKey(option, optionIDField);
                    if (!displayOrderRef.current.has(key)) {
                        displayOrderRef.current.set(key, index);
                    }
                });
                itemsRef.current.classList.add('visible');
                itemsRef.current.style.visibility = 'visible';
                setIsOpen(true);
            } else {
                itemsRef.current.classList.remove('visible');
                itemsRef.current.style.visibility = 'hidden';
                setIsOpen(false);
            }
        };

        if (disabled === false && readOnly === false) {
            labelRef.current.onclick = toggleItems;
        } else {
            labelRef.current.onclick = null;
        }
    }, [disabled, readOnly, allOptions]);

    const orderedOptions = useMemo(() => {
        if (!isOpen) return EMPTY_ARRAY;

        const rankByKey = displayOrderRef.current;
        const getRank = option => {
            const key = getSelectionKey(option, optionIDField);
            return rankByKey.has(key) ? rankByKey.get(key) : rankByKey.size;
        };

        return allOptions
            .filter(option => !pinnedNameSet.has(option.name))
            .sort((a, b) => getRank(a) - getRank(b));
    }, [isOpen, allOptions, pinnedNameSet, optionIDField]);

    const displayOptions = useMemo(() => {
        const term = searchTerm.toLowerCase();
        if (!term) return orderedOptions;

        return orderedOptions.filter(option =>
            option.name.toLowerCase().includes(term),
        );
    }, [orderedOptions, searchTerm]);

    useEffect(() => {
        setRenderLimit(RENDER_BATCH_SIZE);
    }, [isOpen, searchTerm]);

    useEffect(() => {
        const sentinel = loadMoreRef.current;
        if (!sentinel) return undefined;

        const observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) {
                setRenderLimit(limit => limit + RENDER_BATCH_SIZE);
            }
        });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [isOpen, renderLimit, displayOptions.length]);

    const handleOutsideMouseDown = useCallback(event => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target)
        ) {
            itemsRef.current.classList.remove('visible');
            itemsRef.current.style.visibility = 'hidden';
            setIsOpen(false);
        }
    }, []);

    useWindowListener('mousedown', handleOutsideMouseDown);

    const handleCharacterTyped = event => {
        setSearchTerm(event.target.value);
    };

    const handleCheckboxStatus = event => {
        const clickedKey = event.target.value;

        if (controlledSelection) {
            const clickedOption = options.find(
                o => getSelectionKey(o, optionIDField) === clickedKey,
            );
            let newSelection;

            if (event.target.checked) {
                if (
                    exclusiveOptionId &&
                    clickedOption.id === exclusiveOptionId
                ) {
                    newSelection = [clickedOption];
                } else {
                    newSelection = [
                        ...savedOptions.filter(
                            sel => sel.id !== exclusiveOptionId,
                        ),
                        clickedOption,
                    ];
                }
            } else {
                newSelection = savedOptions.filter(
                    sel => sel.id !== clickedOption.id,
                );

                if (exclusiveOptionId && newSelection.length === 0) {
                    const exclusiveOption = options.find(
                        o => o.id === exclusiveOptionId,
                    );
                    if (exclusiveOption) newSelection = [exclusiveOption];
                }
            }

            updateOptions(newSelection);
            return;
        }

        const newAllOptions = [...allOptions];
        if (maxSelections === 1 && event.target.checked) {
            for (let j = 0; j < newAllOptions.length; j++) {
                newAllOptions[j].checked = false;
            }
        }

        for (let i = 0; i < newAllOptions.length; i++) {
            if (
                getSelectionKey(newAllOptions[i], optionIDField) === clickedKey
            ) {
                newAllOptions[i].checked = !newAllOptions[i].checked;
            }
        }
        setAllOptions(newAllOptions);

        const newSelectedArray = [...selectedOptions];
        if (event.target.checked) {
            if (maxSelections === 1) {
                setSelectedOptions([clickedKey]);
            } else {
                newSelectedArray.push(clickedKey);
                setSelectedOptions(newSelectedArray);
            }
        } else {
            newSelectedArray.splice(newSelectedArray.indexOf(clickedKey), 1);
            setSelectedOptions(newSelectedArray);
        }

        returnOptionsArray(newAllOptions);
    };

    const removeOption = optionKey => {
        const matchedOpt = optionsByKey.get(optionKey);
        const optId = matchedOpt
            ? optionIDField
                ? matchedOpt[optionIDField]
                : matchedOpt
            : null;
        if (lockedOptionIds.includes(optId)) return;

        const newSelectedArray = [...selectedOptions];
        newSelectedArray.splice(newSelectedArray.indexOf(optionKey), 1);
        setSelectedOptions(newSelectedArray);

        const newAllOptions = [...allOptions];
        for (let i = 0; i < newAllOptions.length; i++) {
            if (
                getSelectionKey(newAllOptions[i], optionIDField) === optionKey
            ) {
                newAllOptions[i].checked = false;
            }
        }
        setAllOptions(newAllOptions);

        returnOptionsArray(newAllOptions);
    };

    const handleSelectAll = () => {
        const allChecked = allOptions.every(opt => opt.checked);
        const newAllOptions = allOptions.map(opt => ({
            ...opt,
            checked: !allChecked,
        }));
        setAllOptions(newAllOptions);

        const newSelectedOptions = allChecked
            ? []
            : newAllOptions.map(opt => getSelectionKey(opt, optionIDField));
        setSelectedOptions(newSelectedOptions);
        returnOptionsArray(newAllOptions);
    };

    const returnOptionsArray = allOptionsArray => {
        const checkedOptions = allOptionsArray.filter(option => option.checked);
        const checkedByKey = new Map(
            checkedOptions.map(option => [
                getSelectionKey(option, optionIDField),
                option,
            ]),
        );
        const selectedKeySet = new Set(selectedOptions);
        const orderedOptions = selectedOptions
            .map(key => checkedByKey.get(key))
            .filter(Boolean);
        const newlyAdded = checkedOptions.filter(
            opt => !selectedKeySet.has(getSelectionKey(opt, optionIDField)),
        );
        const selectedOptionsData = [...orderedOptions, ...newlyAdded].map(
            option => {
                const result = { ...option };
                delete result.checked;
                return result;
            },
        );
        updateOptions(selectedOptionsData);
    };

    const onDragEnd = result => {
        if (!result.destination) return;

        const items = Array.from(selectedOptions);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setSelectedOptions(items);

        const reorderedSelectedOptions = items
            .map(key => optionsByKey.get(key))
            .filter(Boolean);

        updateOptions(reorderedSelectedOptions);
    };

    const selectedCount = minSelections
        ? allOptions.filter(opt => opt.checked).length
        : 0;

    return (
        <>
            <div
                ref={dropdownRef}
                className="dropdown-check-list"
                style={readOnly && showTags ? { display: 'none' } : {}}
            >
                {buttonMode && (
                    <div
                        onClick={() => {}}
                        type="button"
                        ref={labelRef}
                        className="ing-button-primary-fit-content"
                        style={{ marginBottom: '16px' }}
                    >
                        <i className={buttonIcon + ` me-1`} />
                        <span>{buttonLabel}</span>
                    </div>
                )}

                {!buttonMode && (
                    <div
                        ref={labelRef}
                        className={
                            'dropdown-check-list-label ' +
                            (disabled ? 'disabled' : '')
                        }
                    >
                        {placeholderLabel}
                        <i
                            style={{
                                color: 'var(--iNGAGED-Neutral-Color3)',
                                display: 'flex',
                                marginLeft: 'auto',
                            }}
                            className={'bi bi-chevron-down'}
                        ></i>
                    </div>
                )}

                <div
                    className="mobileDropdown"
                    style={{ position: 'relative' }}
                >
                    <ul ref={itemsRef} className="items">
                        <div className="dropdown-search-bar">
                            <i
                                style={{
                                    color: 'var(--iNGAGED-Neutral-Color3)',
                                    marginRight: '8px',
                                }}
                                className="bi bi-search"
                            />
                            <input
                                style={{ width: '100%' }}
                                onChange={handleCharacterTyped}
                                type="text"
                                placeholder={getText('msg_5616c8722256')}
                            />
                        </div>
                        <hr
                            style={{
                                margin: '0px',
                                color: 'rgba(220, 218, 226, 1)',
                            }}
                        />
                        <div
                            className="item-list"
                            style={{ maxHeight: '200px', overflowY: 'auto' }}
                        >
                            {selectAll && (
                                <div className="dropdown-checkbox-container">
                                    <input
                                        onChange={handleSelectAll}
                                        type="checkbox"
                                        className="form-check-input custom-checkbox"
                                        checked={
                                            allOptions.length > 0 &&
                                            allOptions.every(opt => opt.checked)
                                        }
                                        disabled={disabled}
                                    />
                                    <span>{getText('msg_86a599ef4df2')}</span>
                                </div>
                            )}
                            {displayOptions
                                .slice(0, renderLimit)
                                .map((option, index) => {
                                    const disableThisCheckbox =
                                        minSelections &&
                                        minSelections > 0 &&
                                        selectedCount <= minSelections &&
                                        option.checked;
                                    const optId = optionIDField
                                        ? option[optionIDField]
                                        : option;
                                    const isLockedOption =
                                        lockedOptionIds.includes(optId);
                                    const selectionKey = getSelectionKey(
                                        option,
                                        optionIDField,
                                    );

                                    return (
                                        <div
                                            className="dropdown-checkbox-container"
                                            key={
                                                optionIDField
                                                    ? selectionKey
                                                    : `${selectionKey}-${index}`
                                            }
                                        >
                                            <input
                                                onChange={handleCheckboxStatus}
                                                type="checkbox"
                                                className="form-check-input custom-checkbox"
                                                value={selectionKey}
                                                checked={option.checked}
                                                disabled={
                                                    disableThisCheckbox ||
                                                    isLockedOption
                                                }
                                            />
                                            <span>{option.name}</span>
                                        </div>
                                    );
                                })}
                            {displayOptions.length > renderLimit && (
                                <div ref={loadMoreRef} aria-hidden="true">
                                    &nbsp;
                                </div>
                            )}
                        </div>
                    </ul>
                </div>
            </div>

            {showTags &&
                readOnly &&
                selectedOptions.length === 0 &&
                pinnedOptions.length === 0 && (
                    <span
                        style={{
                            fontStyle: 'italic',
                            color: 'var(--iNGAGED-Neutral-Color2)',
                            margin: '11px 0 0 0',
                        }}
                    >
                        {getText('msg_79dd2019667a')}
                    </span>
                )}

            {showTags &&
                !(
                    readOnly &&
                    selectedOptions.length === 0 &&
                    pinnedOptions.length === 0
                ) && (
                    <div
                        style={{
                            overflow: 'hidden',
                            borderRadius: '12px',
                            border: 'solid 1px rgba(47, 43, 67, 0.2)',
                            marginTop: '10px',
                            boxShadow:
                                'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px',
                        }}
                    >
                        {arrangeTags ? (
                            <DragDropContext onDragEnd={onDragEnd}>
                                <Droppable
                                    droppableId="options"
                                    direction="horizontal"
                                >
                                    {provided => (
                                        <div
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            className={
                                                disabled ? 'disabled' : ''
                                            }
                                            style={{
                                                overflowY: 'auto',
                                                height: '100px',
                                                padding: '16px',
                                                resize: 'none',
                                                boxShadow:
                                                    'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px',
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: '6px',
                                            }}
                                            aria-label={getText(
                                                'msg_82410b800401',
                                            )}
                                        >
                                            {pinnedOptions.map(pinned => (
                                                <div
                                                    key={pinned.name}
                                                    style={{
                                                        display: 'inline-block',
                                                        margin: '0px 6px 6px 0px',
                                                    }}
                                                >
                                                    <span
                                                        className="badge-base badge-population-group"
                                                        style={{
                                                            opacity: 0.65,
                                                        }}
                                                    >
                                                        {pinned.name}
                                                    </span>
                                                </div>
                                            ))}
                                            {selectedOptions.map(
                                                (option, index) => {
                                                    const matchedOpt =
                                                        optionsByKey.get(
                                                            option,
                                                        );
                                                    const optId = matchedOpt
                                                        ? optionIDField
                                                            ? matchedOpt[
                                                                  optionIDField
                                                              ]
                                                            : matchedOpt
                                                        : null;
                                                    const isLocked =
                                                        lockedOptionIds.includes(
                                                            optId,
                                                        );

                                                    return (
                                                        <Draggable
                                                            key={option}
                                                            draggableId={option}
                                                            index={index}
                                                            isDragDisabled={
                                                                readOnly
                                                            }
                                                        >
                                                            {provided => (
                                                                <div
                                                                    ref={
                                                                        provided.innerRef
                                                                    }
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    style={{
                                                                        display:
                                                                            'inline-block',
                                                                        margin: '0px 6px 6px 0px',
                                                                        ...provided
                                                                            .draggableProps
                                                                            .style,
                                                                    }}
                                                                >
                                                                    <span
                                                                        className="badge-base badge-population-group"
                                                                        style={
                                                                            isLocked
                                                                                ? {
                                                                                      opacity: 0.65,
                                                                                  }
                                                                                : {}
                                                                        }
                                                                    >
                                                                        {!isLocked &&
                                                                            !readOnly && (
                                                                                <i
                                                                                    onClick={() =>
                                                                                        removeOption(
                                                                                            option,
                                                                                        )
                                                                                    }
                                                                                    className="bi bi-x-lg"
                                                                                    dataValue={
                                                                                        option
                                                                                    }
                                                                                    style={{
                                                                                        marginRight:
                                                                                            '8px',
                                                                                        cursor: 'pointer',
                                                                                    }}
                                                                                ></i>
                                                                            )}
                                                                        {
                                                                            matchedOpt?.name
                                                                        }
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    );
                                                },
                                            )}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        ) : (
                            <div
                                className={disabled ? 'disabled' : ''}
                                style={{
                                    overflowY: 'auto',
                                    height: '100px',
                                    padding: '16px',
                                    resize: 'none',
                                    boxShadow:
                                        'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0px 1px 3px 0px',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '6px',
                                }}
                                aria-label={getText('msg_82410b800401')}
                            >
                                {selectedOptions.map(option => (
                                    <div
                                        key={option}
                                        style={{
                                            display: 'inline-block',
                                            margin: '0px 6px 6px 0px',
                                        }}
                                    >
                                        <span className="badge-base badge-population-group">
                                            {!readOnly && (
                                                <i
                                                    onClick={() =>
                                                        removeOption(option)
                                                    }
                                                    className="bi bi-x-lg"
                                                    dataValue={option}
                                                    style={{
                                                        marginRight: '8px',
                                                        cursor: 'pointer',
                                                    }}
                                                ></i>
                                            )}
                                            {optionsByKey.get(option)?.name}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
        </>
    );
}
